import React, { useState, useEffect } from 'react';
import { assetsAPI } from '../utils/api';
import { Link } from 'react-router-dom';
import { Search, Plus, Package, Wrench, AlertTriangle, LayoutGrid, List } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';

const Assets = () => {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [formData, setFormData] = useState({
    name: '',
    asset_type: '',
    serial_number: '',
    purchase_cost: 0,
    condition: 'good',
    location: 'store',
    notes: '',
    // Vehicle fields
    is_vehicle: false,
    vehicle_registration: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_mileage: '',
    vehicle_fuel_type: '',
    vehicle_license_expiry: '',
    // Maintenance fields
    maintenance_interval_days: '',
  });

  useEffect(() => {
    loadAssets();
  }, []);

  useEffect(() => {
    filterAssets();
  }, [assets, searchTerm, statusFilter]);

  const loadAssets = async () => {
    try {
      const response = await assetsAPI.getAll();
      setAssets(response.data);
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const filterAssets = () => {
    let filtered = [...assets];

    if (searchTerm) {
      filtered = filtered.filter(
        (asset) =>
          asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asset.asset_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (asset.serial_number && asset.serial_number.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((asset) => asset.status === statusFilter);
    }

    setFilteredAssets(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await assetsAPI.create(formData);
      toast.success('Asset created successfully');
      setShowCreateModal(false);
      setFormData({
        name: '',
        asset_type: '',
        serial_number: '',
        purchase_cost: 0,
        condition: 'good',
        location: 'store',
        notes: '',
        is_vehicle: false,
        vehicle_registration: '',
        vehicle_make: '',
        vehicle_model: '',
        vehicle_year: '',
        vehicle_mileage: '',
        vehicle_fuel_type: '',
        vehicle_license_expiry: '',
        maintenance_interval_days: '',
      });
      loadAssets();
    } catch (error) {
      toast.error('Failed to create asset');
    }
  };

  const statusColors = {
    available: 'bg-green-100 text-green-800 border-green-200',
    issued: 'bg-blue-100 text-blue-700 border-blue-200',
    maintenance: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    retired: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  const conditionColors = {
    good: 'bg-green-50 text-green-700',
    fair: 'bg-yellow-50 text-yellow-700',
    poor: 'bg-red-50 text-red-700',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading assets...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="assets-page" className="p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Asset Register
          </h1>
          <p className="text-slate-600">{filteredAssets.length} assets found</p>
        </div>
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <button
            data-testid="create-asset-button"
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg shadow-sm transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Asset
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                data-testid="search-input"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search assets..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>
          </div>
          <div>
            <select
              data-testid="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="all">All Statuses</option>
              <option value="available">Available</option>
              <option value="issued">Issued</option>
              <option value="maintenance">Maintenance</option>
              <option value="retired">Retired</option>
            </select>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              title="Grid View"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              title="List View"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAssets.length > 0 ? (
            filteredAssets.map((asset) => (
              <Link key={asset.id} to={`/assets/${asset.id}`} data-testid={`asset-item-${asset.id}`}>
                <div className="job-card bg-white border border-slate-200 shadow-sm rounded-xl p-6 hover:shadow-md transition-all h-full">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Package className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <span className="text-xs font-mono font-bold text-blue-600 block mb-0.5">
                          {asset.asset_code || 'N/A'}
                        </span>
                        <h3 className="font-semibold text-slate-900 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {asset.name}
                        </h3>
                        <p className="text-sm text-slate-600">{asset.asset_type}</p>
                      </div>
                    </div>
                  </div>
                  {asset.serial_number && (
                    <p className="text-xs text-slate-500 mb-3 font-mono">SN: {asset.serial_number}</p>
                  )}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                          statusColors[asset.status] || statusColors.available
                        }`}
                      >
                        {asset.status}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${conditionColors[asset.condition]}`}>
                        {asset.condition}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Location:</span>
                      <span className="font-medium text-slate-900">{asset.location}</span>
                    </div>
                    {asset.purchase_cost > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Value:</span>
                        <span className="font-medium text-slate-900">{formatCurrency(asset.purchase_cost)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-slate-500 text-lg">No assets found matching your filters</p>
            </div>
          )}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Asset ID</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Asset</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Serial #</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Condition</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Location</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredAssets.length > 0 ? (
                filteredAssets.map((asset) => (
                  <tr key={asset.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-xs font-mono font-bold text-blue-600">{asset.asset_code || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <Link to={`/assets/${asset.id}`} className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Package className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{asset.name}</p>
                          <p className="text-sm text-slate-500">{asset.asset_type}</p>
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-mono">{asset.serial_number || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[asset.status] || statusColors.available}`}>
                        {asset.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs px-2 py-1 rounded ${conditionColors[asset.condition]}`}>
                        {asset.condition}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{asset.location}</td>
                    <td className="px-6 py-4 text-sm text-slate-900 text-right font-medium">
                      {asset.purchase_cost > 0 ? formatCurrency(asset.purchase_cost) : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                    No assets found matching your filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="create-asset-modal">
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Add New Asset
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Asset Name *</label>
                  <input
                    data-testid="asset-name-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="e.g., Extension Ladder"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Asset Type *</label>
                  <input
                    data-testid="asset-type-input"
                    type="text"
                    value={formData.asset_type}
                    onChange={(e) => setFormData({ ...formData, asset_type: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="e.g., Ladder, Drill, Wheelbarrow"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Serial Number</label>
                  <input
                    data-testid="serial-number-input"
                    type="text"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="e.g., LDR-2024-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Purchase Cost (R)</label>
                  <input
                    data-testid="purchase-cost-input"
                    type="text"
                    value={formData.purchase_cost_str !== undefined ? formData.purchase_cost_str : (formData.purchase_cost || '')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setFormData({ 
                        ...formData, 
                        purchase_cost_str: value,
                        purchase_cost: value ? parseFloat(value) : 0 
                      });
                    }}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Condition</label>
                  <select
                    data-testid="condition-select"
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  >
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                  <input
                    data-testid="location-input"
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    placeholder="e.g., Store, Warehouse A"
                  />
                </div>
              </div>

              {/* Vehicle Toggle */}
              <div className="flex items-center gap-3 py-3 border-t border-slate-200 mt-4">
                <input
                  type="checkbox"
                  id="is_vehicle"
                  checked={formData.is_vehicle}
                  onChange={(e) => setFormData({ ...formData, is_vehicle: e.target.checked, asset_type: e.target.checked ? 'vehicle' : formData.asset_type })}
                  className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                />
                <label htmlFor="is_vehicle" className="text-sm font-medium text-slate-700">
                  This is a vehicle
                </label>
              </div>

              {/* Vehicle Fields - Only show when is_vehicle is true */}
              {formData.is_vehicle && (
                <div className="bg-blue-50 p-4 rounded-lg space-y-4 border border-blue-100">
                  <h4 className="font-medium text-blue-900 text-sm">Vehicle Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Registration Number</label>
                      <input
                        type="text"
                        value={formData.vehicle_registration}
                        onChange={(e) => setFormData({ ...formData, vehicle_registration: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        placeholder="e.g., ABC 123 GP"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Make</label>
                      <input
                        type="text"
                        value={formData.vehicle_make}
                        onChange={(e) => setFormData({ ...formData, vehicle_make: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        placeholder="e.g., Toyota"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Model</label>
                      <input
                        type="text"
                        value={formData.vehicle_model}
                        onChange={(e) => setFormData({ ...formData, vehicle_model: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        placeholder="e.g., Hilux"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Year</label>
                      <input
                        type="text"
                        value={formData.vehicle_year}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFormData({ ...formData, vehicle_year: value });
                        }}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        placeholder="e.g., 2022"
                        maxLength={4}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Mileage (km)</label>
                      <input
                        type="text"
                        value={formData.vehicle_mileage}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFormData({ ...formData, vehicle_mileage: value });
                        }}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        placeholder="e.g., 50000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Fuel Type</label>
                      <select
                        value={formData.vehicle_fuel_type}
                        onChange={(e) => setFormData({ ...formData, vehicle_fuel_type: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      >
                        <option value="">Select Fuel Type</option>
                        <option value="petrol">Petrol</option>
                        <option value="diesel">Diesel</option>
                        <option value="electric">Electric</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">License Expiry Date</label>
                      <input
                        type="date"
                        value={formData.vehicle_license_expiry}
                        onChange={(e) => setFormData({ ...formData, vehicle_license_expiry: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Maintenance Interval */}
              <div className="py-3 border-t border-slate-200 mt-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Maintenance Interval (days)</label>
                <input
                  type="text"
                  value={formData.maintenance_interval_days}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setFormData({ ...formData, maintenance_interval_days: value });
                  }}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="e.g., 90 (days between maintenance)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  data-testid="notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="Any additional notes..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  data-testid="submit-asset-button"
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg shadow-sm transition-colors"
                >
                  Create Asset
                </button>
                <button
                  data-testid="cancel-asset-button"
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assets;
